import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import useDocumentMetadata from '../hooks/useDocumentMetadata';
import FluidSubpageHero from '../components/FluidSubpageHero';

export default function Cookiepolitik() {
  const { lang } = useLanguage();

  // SEO Metadata
  const seoTitle = lang === 'da' 
    ? 'Cookiepolitik | Nordisk Psykiatri' 
    : 'Cookiepolicy | Nordisk Psykiatri';

  const seoDesc = lang === 'da'
    ? 'Læs vores cookiepolitik om hvordan vi anvender cookies og beskytter dit privatliv på vores hjemmeside.'
    : 'Läs vår cookiepolicy om hur vi använder cookies och skyddar din integritet på vår webbplats.';

  useDocumentMetadata(seoTitle, seoDesc, { lang });

  return (
    <div className="flex flex-col min-h-screen bg-sand-light">
      {/* HERO */}
      <FluidSubpageHero
        className="bg-[#f6f0e2]"
        style={{ backgroundColor: '#f6f0e2' }}
        eyebrow={lang === 'da' ? 'VILKÅR & POLITIKKER' : 'VILLKOR & POLICYER'}
        title={lang === 'da' ? 'Cookiepolitik' : 'Cookiepolicy'}
        description={lang === 'da' 
          ? 'Denne cookiepolitik forklarer, hvordan Nordisk Psykiatri anvender cookies og lignende teknologier på vores hjemmeside.'
          : 'Denna cookiepolicy förklarar hur Nordisk Psykiatri använder cookies och liknande teknologier på vår webbplats.'}
      />

      {/* Clean, Simple Terms Content */}
      <section className="py-12 md:py-16 bg-white">
        <div className="mx-auto max-w-4xl px-6">
          {/* Sections */}
          <div className="space-y-12">
            
            {/* Section 1 */}
            <div className="text-left">
              <h2 className="font-display text-xl font-light text-petrol-dark mb-4 uppercase tracking-wide text-sm md:text-base">
                {lang === 'da' ? '1. Hvad er cookies?' : '1. Vad är cookies?'}
              </h2>
              <div className="space-y-4 text-base text-[#2d3237] leading-relaxed font-light">
                <p>
                  {lang === 'da'
                    ? 'Cookies er små tekstfiler, der gemmes på din computer, tablet eller mobil, når du besøger en hjemmeside. Cookies gør det muligt for hjemmesiden at genkende din enhed og huske dine indstillinger, så vi kan forbedre din brugeroplevelse.'
                    : 'Cookies är små textfiler som sparas på din dator, surfplatta eller mobil när du besöker en webbplats. Cookies gör det möjligt för webbplatsen att känna igen din enhet och komma ihåg dina inställningar för att förbättra din användarupplevelse.'}
                </p>
              </div>
            </div>

            <hr className="my-10 border-[#ece8e1]" />

            {/* Section 2 */}
            <div className="text-left">
              <h2 className="font-display text-xl font-light text-petrol-dark mb-4 uppercase tracking-wide text-sm md:text-base">
                {lang === 'da' ? '2. Hvilke cookies bruger vi?' : '2. Vilka cookies använder vi?'}
              </h2>
              <div className="space-y-4 text-base text-[#2d3237] leading-relaxed font-light">
                <p>
                  {lang === 'da'
                    ? 'Vi anvender cookies til følgende formål:'
                    : 'Vi använder cookies för följande ändamål:'}
                </p>
                <div className="space-y-6 pl-4 border-l-2 border-petrol-medium/20 my-4">
                  <div>
                    <h3 className="font-semibold text-petrol-dark mb-1">
                      {lang === 'da' ? 'Nødvendige cookies' : 'Nödvändiga cookies'}
                    </h3>
                    <p>
                      {lang === 'da'
                        ? 'Disse cookies er nødvendige for, at hjemmesiden fungerer korrekt. De kan ikke fravælges.'
                        : 'Dessa cookies är nödvändiga för att webbplatsen ska fungera korrekt. De kan inte väljas bort.'}
                    </p>
                  </div>
                  <div>
                    <h3 className="font-semibold text-petrol-dark mb-1">
                      {lang === 'da' ? 'Funktionelle cookies' : 'Funktionella cookies'}
                    </h3>
                    <p>
                      {lang === 'da'
                        ? 'Disse cookies husker dine valg og indstillinger (f.eks. sprog eller formularfelter) for at give dig en bedre og mere personlig oplevelse.'
                        : 'Dessa cookies kommer ihåg dina val och inställningar (t.ex. språk eller formulärfält) för att ge dig en bättre och mer personlig upplevelse.'}
                    </p>
                  </div>
                  <div>
                    <h3 className="font-semibold text-petrol-dark mb-1">
                      {lang === 'da' ? 'Statistiske cookies' : 'Statistiska cookies'}
                    </h3>
                    <p>
                      {lang === 'da'
                        ? 'Disse cookies hjælper os med at forstå, hvordan vores hjemmeside anvendes, så vi kan forbedre indhold og funktionalitet. Vi indsamler data anonymt og deler ikke oplysninger med tredjepart.'
                        : 'Dessa cookies hjälper oss att förstå hur vår webbplats används så att vi kan förbättra innehåll och funktioner. Vi samlar in data anonymt och delar inte information med tredje part.'}
                    </p>
                  </div>
                </div>
                <p className="pt-2">
                  {lang === 'da'
                    ? 'Vi anvender cookies fra følgende tjenesteudbydere:'
                    : 'Vi använder cookies från följande tjänsteleverantörer:'}
                </p>
                <ul className="list-disc pl-6 space-y-1">
                  <li>Google Analytics ({lang === 'da' ? 'anonymiseret' : 'anonymiserad'})</li>
                  <li>Planway ({lang === 'da' ? 'booking- og journalsystem' : 'boknings- och journalsystem'})</li>
                </ul>
              </div>
            </div>

            <hr className="my-10 border-[#ece8e1]" />

            {/* Section 3 */}
            <div className="text-left">
              <h2 className="font-display text-xl font-light text-petrol-dark mb-4 uppercase tracking-wide text-sm md:text-base">
                {lang === 'da' ? '3. Dine valgmuligheder' : '3. Dina valmöjligheter'}
              </h2>
              <div className="space-y-4 text-base text-[#2d3237] leading-relaxed font-light">
                <p>
                  {lang === 'da'
                    ? 'Du kan til enhver tid ændre eller trække dit samtykke tilbage via cookieindstillingerne nederst til venstre på vores hjemmeside.'
                    : 'Du kan när som helst ändra eller återkalla ditt samtycke via cookieinställningarna längst ner till vänster på vår webbplats.'}
                </p>
                <p>
                  {lang === 'da'
                    ? 'Du kan også slette eller blokere cookies i dine browserindstillinger. Vær opmærksom på, at visse funktioner på hjemmesiden muligvis ikke fungerer optimalt, hvis du blokerer cookies.'
                    : 'Du kan också radera eller blockera cookies i dina webbläsarinställningar. Observera att vissa funktioner på webbplatsen kanske inte fungerar optimalt om du blockerar cookies.'}
                </p>
              </div>
            </div>

            <hr className="my-10 border-[#ece8e1]" />

            {/* Section 4 */}
            <div className="text-left">
              <h2 className="font-display text-xl font-light text-petrol-dark mb-4 uppercase tracking-wide text-sm md:text-base">
                {lang === 'da' ? '4. Ændringer' : '4. Ändringar'}
              </h2>
              <div className="space-y-4 text-base text-[#2d3237] leading-relaxed font-light">
                <p>
                  {lang === 'da'
                    ? 'Vi kan løbende opdatere denne cookiepolitik. Den seneste version vil altid være tilgængelig på denne side.'
                    : 'Vi kan löpande uppdatera denna cookiepolicy. Den senaste versionen kommer alltid att finnas tillgänglig på denna sida.'}
                </p>
              </div>
            </div>

            <hr className="my-10 border-[#ece8e1]" />

            {/* Section 5 */}
            <div className="text-left">
              <h2 className="font-display text-xl font-light text-petrol-dark mb-4 uppercase tracking-wide text-sm md:text-base">
                {lang === 'da' ? '5. Kontakt' : '5. Kontakt'}
              </h2>
              <div className="space-y-4 text-base text-[#2d3237] leading-relaxed font-light">
                <p>
                  {lang === 'da'
                    ? 'Har du spørgsmål til vores brug af cookies, er du velkommen til at kontakte os:'
                    : 'Har du frågor om vår användning av cookies är du välkommen att kontakta oss:'}
                </p>
                <div className="pl-4 border-l-2 border-petrol-medium/30 my-4 space-y-1">
                  <p className="font-medium text-petrol-dark">Nordisk Psykiatri</p>
                  <p>Vesterbrogade 37, 1. tv</p>
                  <p>1620 København V</p>
                  <p>
                    {lang === 'da' ? 'Telefon: ' : 'Telefon: '}
                    <a href="tel:+4523957707" className="text-petrol-medium hover:text-petrol-dark transition-colors font-medium">
                      +45 23 95 77 07
                    </a>
                  </p>
                  <p>
                    {lang === 'da' ? 'E-mail: ' : 'E-post: '}
                    <a href="mailto:kontakt@nordiskpsykiatri.dk" className="text-petrol-medium hover:text-petrol-dark transition-colors font-medium">
                      kontakt@nordiskpsykiatri.dk
                    </a>
                  </p>
                </div>
                <p className="text-sm text-text-dark/50 mt-6 italic">
                  {lang === 'da'
                    ? 'Denne cookiepolitik er senest opdateret den 15. maj 2024.'
                    : 'Denna cookiepolicy uppdaterades senast den 15 maj 2024.'}
                </p>
              </div>
            </div>

          </div>
        </div>
      </section>
    </div>
  );
}
